import type { Logger } from '@interopio/gateway/logging/api';
import { hrwHash } from 'hrw-hash';
import { LamportClock, type LamportTimestamp } from './lamport.ts';
import type { Node, NodeAnnouncement, NodeConnection, NodeConnections, NodeEventTypes } from './types.ts';
import { EventEmitter } from 'node:events';

type NodeValue = Omit<Node, 'users'> & {
    node: string
    readonly firstSeen: LamportTimestamp
    status: 'announced' | 'added' | 'expired' | 'removed'
    owner: string
    replicas: readonly string[]
    users: Set<string>
    lastSeen: number
    relayOwner?: string
    readonly metadata?: { [key: string]: unknown }
}

export default class InMemoryNodeConnections implements NodeConnections {
    readonly #logger: Logger;
    readonly #eventEmitter = new EventEmitter<NodeEventTypes>();
    readonly #nodes = new Map<string, NodeValue>();
    readonly #clusterNodes: string[];
    readonly #lamport: LamportClock;
    readonly #timeout: number;

    constructor(logger: Logger,
                instanceId: string,
                timeout: number = 60000) {
        this.#logger = logger;
        this.#clusterNodes = [instanceId];
        this.#lamport = new LamportClock(instanceId);
        this.#timeout = timeout;
    }

    async init(gossipTimestamp = 0) {
        await this.#lamport.init(gossipTimestamp);
    }

    async close() {
        await this.#lamport.shutdown();
    }

    announce(announcements: Array<NodeAnnouncement>): NodeConnection[] {
        const now = Date.now();
        for (const announcement of announcements) {
            const { node: nodeId, users, endpoint: announcedEndpoint, metadata } = announcement;

            const replicas : readonly string[] = hrwHash(nodeId, this.#clusterNodes);
            const owner = replicas[0];
            const externalEndpoint = announcedEndpoint !== undefined && announcedEndpoint !== `/cluster?node=${nodeId}`;
            const endpoint = externalEndpoint ? announcedEndpoint : `/cluster?node=${nodeId}&owner=${owner}`;

            let node: NodeValue = this.#nodes.get(nodeId);
            const userSet = new Set<string>(users ?? []);
            if (!node) {
                const lamport = this.#lamport.tick();
                node = {node: nodeId, users: userSet, firstSeen: lamport, lastSeen: now, owner, replicas, endpoint, metadata, status: 'announced' };
                this.#nodes.set(nodeId, node);
                this.#logger.info(`${nodeId} announced at endpoint ${endpoint} with meta: ${JSON.stringify(metadata)}`);
            }

            node.owner = owner;
            if (!externalEndpoint) {
                node.endpoint = endpoint;
            }
            node.replicas = replicas;
            node.lastSeen = now;
            if (!(node.status === 'announced' || node.status === 'added')) {
                node.status = 'announced';
            }
            node.users = userSet;

            this.#eventEmitter.emit('node-announced', nodeId, endpoint, userSet);
        }
        const sortedNodes = this.#sortedNodeValues();
        return announcements.map((e) => {
            const node  = this.#nodes.get(e.node);
            const connect = this.#findConnections(sortedNodes, node);
            return { node: node.node, owner: node.owner, connect };
        });
    }

    find(nodeId: string): ReadonlyArray<Node> | undefined {
        const e = this.#nodes.get(nodeId);
        if (e !== undefined) {
            const sortedNodes = this.#sortedNodeValues();
            return this.#findConnections(sortedNodes, e);
        }
        return undefined;
    }

    add(nodeId: string) {
        const added = this.#nodes.get(nodeId);
        if (added !== undefined && (added.status === 'announced' || added.status === 'removed')) {
            added.status = 'added';
            this.#logger.info(`endpoint ${added.endpoint} connected for ${nodeId}`);
            this.#eventEmitter.emit('node-connected', nodeId);
            return true;
        }
        return false;
    }

    remove(nodeId: string) {
        const removed = this.#nodes.get(nodeId);
        if (removed !== undefined && (removed.status === 'announced' || removed.status === 'added')) {
            removed.status = 'removed';
            this.#logger.info(`endpoint ${removed.endpoint} removed for ${nodeId}`);
            this.#eventEmitter.emit('node-disconnected', nodeId, 'removed');
            return true;
        }
        return false;
    }

    #sortedNodeValues(): NodeValue[] {
        this.#cleanupOldNodes();
        return Array.from(this.#nodes.values())
            .filter((n) => n.status === 'announced' || n.status === 'added')
            .sort((a, b) => {
                const diff = a.firstSeen.timestamp - b.firstSeen.timestamp;
                if (diff === 0) {
                    return a.node.localeCompare(b.node);
                }
                return diff;
            });
    }

    #cleanupOldNodes() {
        const now = Date.now();
        const expiredTimestamp = now - this.#timeout;
        const removedTimestamp = now - (this.#timeout * 2);
        const compactTimestamp = now - (this.#timeout * 10);
        for (const [nodeId, v] of this.#nodes) {
            if (v.lastSeen < compactTimestamp && v.status === 'removed') {
                if (this.#logger.enabledFor('debug')) {
                    this.#logger.debug(`${nodeId} compact - no announcement since ${new Date(v.lastSeen).toISOString()}, timeout is ${this.#timeout} ms and status is ${v.status}.`);
                }
                this.#nodes.delete(nodeId);
            }
            if (v.lastSeen < removedTimestamp && v.status === 'expired') {
                if (this.#logger.enabledFor('debug')) {
                    this.#logger.debug(`${nodeId} removed - no announcement since ${new Date(v.lastSeen).toISOString()}, timeout is ${this.#timeout} ms.`);
                }
                // mark as removed
                v.status = 'removed';
            }

            if (v.lastSeen < expiredTimestamp && (v.status === 'announced' || v.status === 'added')) {
                if (this.#logger.enabledFor('debug')) {
                    this.#logger.debug(`${nodeId} expired - no announcement since ${new Date(v.lastSeen).toISOString()}, timeout is ${this.#timeout} ms.`);
                }
                // mark as expired
                v.status = 'expired';
                this.#eventEmitter.emit('node-disconnected', nodeId, 'expired');
            }
        }
    }

    public on<K extends keyof NodeEventTypes>(event: K, listener: (K extends keyof NodeEventTypes ? NodeEventTypes[K] extends unknown[] ? (...args: NodeEventTypes[K]) => void : never : never)): this {
        this.#eventEmitter.on(event, listener);
        return this;
    }

    public off<K extends keyof NodeEventTypes>(event: K, listener: (K extends keyof NodeEventTypes ? NodeEventTypes[K] extends unknown[] ? (...args: NodeEventTypes[K]) => void : never : never)): this {
        this.#eventEmitter.off(event, listener);
        return this;
    }

    #findConnections(sortedNodes: NodeValue[], node?: NodeValue): ReadonlyArray<Node> {
        const connections = sortedNodes.reduce((l, c) => {
            if (node !== undefined && c.firstSeen.timestamp < node.firstSeen.timestamp) {
                const intersection = new Set(c.users);
                node.users.forEach(user => {
                    if (!c.users.has(user)) {
                        intersection.delete(user);
                    }
                });
                c.users.forEach(user => {
                    if (!node.users.has(user)) {
                        intersection.delete(user);
                    }
                });
                if (intersection.size > 0) {
                    const e: Node = { node: c.node, endpoint: c.endpoint, owner: c.owner };
                    return l.concat(e);
                }
            }
            return l;
        }, new Array<Node>());
        if (this.#logger.enabledFor('debug')) {
            this.#logger.debug(`found ${connections.length} connection(s) for node ${node?.node}: [${connections.map((e) => `${e.node}@${e.endpoint}`).join(', ')}]`);
        }
        return connections;
    }
}
